import { Box, Button, Link } from '@material-ui/core';
import {
  addShoppingCart,
  cleanShopifyProductDetail,
  sendShopifyProduct,
  setShopifyVariantId,
  setShoppingTabView,
  updateCarouselImage,
} from '@reducers/shopify';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import { ShopifyTabsView } from '@src/reducers/shopify/types';
import { isChatComposerDisabled } from '@src/selectors/agentConsole';
import {
  GetProductDetails,
  getCarouselImagesByVariantId,
  getProductDetails,
  getProductUrl,
  getSelectedVariantId,
  getShopifyCurrentStoreCanAddItemOutOfStock,
  getShopifyShoppingCartProductQuantity,
  getShopifyShoppingCartTotalItems,
  getVariantByTitle,
} from '@src/selectors/shopify';
import type { RootState } from '@src/store/reduxstore';
import { ShopifyProductImage, ShopifyProductOrigin } from '@src/types/shopify';
import CarouselImage from '@src/utils/CarouselImage';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import showdown from 'showdown';
import { CustomButton } from './Product';
import ShoppingCartCounter from './ShoppingCartCounter';
import { GoBackIcon, ShoppingCartIcon, addProductIcon } from './SvgIcons';
import { Variant } from './Utils';

export interface IProductDetails {
  product: GetProductDetails;
  images: ShopifyProductImage[];
  price: string;
  currencySymbol: string;
  inventoryQuantity: number;
  labels: { [key: string]: string };
  selectedVariantArrId: number[];
  productUrl: string;
  handleClickOnBack: () => void;
  handleClickOnVariant: (indexOption: number, indexValue: number) => void;
  handleClickOnSend: () => void;
  handleUpdateCarouselImage: (value: string) => void;
  handleToggleShoppingCart: () => void;
  handleClickOnAddProduct: () => void;
  disableSendButtons: boolean;
  disableAddProductButton: boolean;
  totalProductsCounter: number;
}

const ProductDetailsWrapper: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { formatMessage } = intl;

  const product = useSelector(getProductDetails);
  const variant = useSelector(getVariantByTitle);

  const selectedVariantArrId = useSelector(getSelectedVariantId);
  const variantImages = useSelector(getCarouselImagesByVariantId);
  const { id } = product;
  const disableSendButtons = useSelector(isChatComposerDisabled);
  const productUrl = useSelector(getProductUrl);

  const {
    price,
    inventoryQuantity,
    id: adminGraphqlApiId,
    currencySymbol,
  } = variant;

  const shoppingCartProductQuantity = useSelector((state: RootState) =>
    getShopifyShoppingCartProductQuantity(state, `${adminGraphqlApiId}`)
  );

  const canAddItemOutOfStock = useSelector(
    getShopifyCurrentStoreCanAddItemOutOfStock
  );

  const totalProductsCounter = useSelector(getShopifyShoppingCartTotalItems);

  const labels = {
    sendButton: formatMessage({
      id: 'Shopify.product.button.send.label',
    }),
    goBack: formatMessage({
      id: 'Shopify.product.button.goBack.label',
    }),
    productPrice: formatMessage({
      id: 'Shopify.product.price.label',
    }),
    availability: formatMessage({
      id: 'Shopify.product.availability.label',
    }),
    units: formatMessage({
      id: 'Shopify.product.availability.units.label',
    }),
    noInventory: formatMessage({ id: 'Shopify.product.noInventory.label' }),
    vendorLabel: formatMessage({ id: 'Shopify.product.provider.label' }),
    collectionLabel: formatMessage({ id: 'Shopify.product.collections.label' }),
  };

  const handleClickOnBack = () => {
    dispatch(cleanShopifyProductDetail());
  };

  const handleClickOnVariant = (indexOption: number, indexValue: number) => {
    dispatch(setShopifyVariantId({ indexOption, indexValue }));
  };

  const handleClickOnSend = () =>
    dispatch(
      sendShopifyProduct({
        sendVariants: true,
        origin: ShopifyProductOrigin.DETAIL,
      })
    );

  const handleUpdateCarouselImage = (imageUrl: string) => {
    dispatch(updateCarouselImage({ productId: id, imageUrl }));
  };

  const handleToggleShoppingCart = () => {
    dispatch(setShoppingTabView(ShopifyTabsView.SHOPPING_CART));
  };

  const handleClickOnAddProduct = () => {
    dispatch(
      addShoppingCart({
        variantId: `${adminGraphqlApiId}`,
        quantity: 1,
        quantityTotal: inventoryQuantity,
        origin: ShopifyProductOrigin.DETAIL,
      })
    );
  };

  return (
    <ProductDetails
      product={product}
      images={variantImages}
      price={price}
      currencySymbol={currencySymbol}
      inventoryQuantity={inventoryQuantity}
      labels={labels}
      selectedVariantArrId={selectedVariantArrId}
      productUrl={productUrl}
      handleClickOnBack={handleClickOnBack}
      handleClickOnVariant={handleClickOnVariant}
      handleClickOnSend={handleClickOnSend}
      handleUpdateCarouselImage={handleUpdateCarouselImage}
      handleToggleShoppingCart={handleToggleShoppingCart}
      handleClickOnAddProduct={handleClickOnAddProduct}
      disableSendButtons={disableSendButtons}
      disableAddProductButton={
        shoppingCartProductQuantity >= inventoryQuantity &&
        !canAddItemOutOfStock
      }
      totalProductsCounter={totalProductsCounter}
    />
  );
};

export const ProductDetails: React.FC<IProductDetails> = ({
  product,
  images,
  price,
  currencySymbol,
  inventoryQuantity,
  labels,
  selectedVariantArrId,
  productUrl,
  handleClickOnBack,
  handleClickOnVariant,
  handleClickOnSend,
  handleUpdateCarouselImage,
  handleToggleShoppingCart,
  handleClickOnAddProduct,
  disableSendButtons,
  disableAddProductButton,
  totalProductsCounter,
}) => {
  const {
    sendButton,
    goBack,
    availability,
    units,
    productPrice,
    noInventory,
    vendorLabel,
    collectionLabel,
  } = labels;
  const {
    title,
    bodyHtml: description,
    options,
    vendor,
    collections,
    variants,
  } = product;

  const hasVariants = variants.length > 1;
  const strikethrough = product?.strikethrough || [];
  const converter = new showdown.Converter();

  const formattedPrice = useCurrencyFormat({
    value: price,
    symbol: currencySymbol,
  });

  return (
    <Box>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="start"
        m={2}
      >
        <Box display="flex" alignItems="center" style={{ gap: '16px' }}>
          <Box onClick={handleClickOnBack} style={{ cursor: 'pointer' }}>
            {GoBackIcon}
          </Box>
          <Box color="text.primary" fontSize="14px" lineHeight="140%">
            {goBack}
          </Box>
        </Box>

        <Button
          color="primary"
          variant="contained"
          onClick={handleToggleShoppingCart}
          disabled={disableSendButtons}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '55px',
            height: '32px',
            borderRadius: '8px',
            marginRight: '16px',
          }}
        >
          {ShoppingCartIcon}
          <ShoppingCartCounter totalProductsCounter={totalProductsCounter} />
        </Button>
      </Box>
      <Box
        color="text.primary"
        fontWeight="500"
        fontSize="16px"
        textAlign="center"
      >
        <Link
          href={productUrl}
          target="_blanck"
          rel="noopener"
          data-testid="shopify-product-details-title-link"
        >
          {title}
        </Link>
      </Box>
      <Box padding="0px 16px 16px 16px" textAlign="center">
        <CarouselImage
          images={images.map(img => img.src)}
          style={{
            boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.08)',
            borderRadius: '8px',
          }}
          handleUpdateCarouselImage={handleUpdateCarouselImage}
        />
      </Box>
      <Box padding="0px 16px 16px 16px">
        <Box color="text.primary" fontWeight="400" fontSize="12px">
          <div
            className="product-details-content"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: converter.makeHtml(description.replace(/[\r\n]/gm, '')),
            }}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          style={{ gap: '16px' }}
          marginTop={2}
          marginBottom={2}
        >
          {hasVariants &&
            options.map(({ name, values }, optionIndex) => (
              <Variant
                key={name}
                name={name}
                strikethrough={strikethrough[optionIndex]}
                values={values}
                selectedValueIndex={selectedVariantArrId[optionIndex]}
                handleOnClick={valueIndex =>
                  handleClickOnVariant(optionIndex, valueIndex)
                }
              />
            ))}
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Box>
            <Box fontWeight={500}>{productPrice}:</Box>
            <Box paddingTop={1}>{formattedPrice}</Box>
          </Box>
          <Box>
            <Box fontWeight={500}>{availability}:</Box>
            <Box paddingTop={1}>
              {!inventoryQuantity ? (
                <Box fontWeight={600} fontSize={12}>
                  {noInventory}
                </Box>
              ) : (
                `${inventoryQuantity} ${units}`
              )}
            </Box>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" paddingTop={2}>
          <Box fontWeight={500}>{`${vendorLabel}:`}</Box>
          <Box fontSize={12} paddingLeft={0.5}>
            {vendor}
          </Box>
        </Box>
        {collections && collections.length > 0 && (
          <Box display="flex" alignItems="center" paddingTop={1}>
            <Box fontWeight={500}>{`${collectionLabel}:`}</Box>
            <Box fontSize={12} paddingLeft={0.5}>
              {collections.map(item => item.name).join(', ')}
            </Box>
          </Box>
        )}
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        padding={2}
        style={{ gap: 20 }}
      >
        <CustomButton
          fullWidth
          onClick={handleClickOnSend}
          disabled={disableSendButtons}
          data-testid="shopify-product-details-send-button"
        >
          {sendButton}
        </CustomButton>

        <CustomButton
          disabled={disableSendButtons || disableAddProductButton}
          onClick={handleClickOnAddProduct}
          data-testid="shopify-product-details-add-button"
        >
          {addProductIcon}
        </CustomButton>
      </Box>
    </Box>
  );
};

export default ProductDetailsWrapper;
